<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import axios from 'axios';

const PLAN_ORDER = ['basic', 'essentials', 'pro'];
const CADENCE_ORDER = ['monthly', 'yearly', 'lifetime'];

const isLoading = ref(true);
const showConfetti = ref(false);
const subscription = ref({
    plan: 'basic',
    status: 'inactive',
    cadence: null,
    access_until: null,
    grace_until: null,
    cancel_at: null,
    canceled_at: null,
    is_canceling: false,
});
const plans = ref([]);
const showFaq = ref(false);
const errorMessage = ref(null);
const successMessage = ref(null);
const selectedCadence = reactive({});
const needsPaymentMethod = ref(false);
const setupUrl = ref(null);
const isCancelling = ref(false);

const featureMatrix = {
    basic: [
        'Core CRM with up to 250 active contacts',
        'Public website builder with 2 live pages',
        'Chat support',
    ],
    essentials: [
        '...Everything in Basic and:',
        'Unlimited CRM contacts and pipelines',
        'Automated email journeys & scheduling assistant',
        'Landing pages and website on your domain',
    ],
    pro: [
        '...Everything in Essentials and:',
        'Contract e-signing with BankID & advanced workflows',
        'LeadHunter automations & AI outreach tools',
        'Unlimited webinars, coaching, and Stripe payments',
    ],
};

const planRank = (plan) => {
    const index = PLAN_ORDER.indexOf(plan ?? 'basic');
    return index === -1 ? 0 : index;
};

const currentPlanRank = computed(() => planRank(subscription.value.plan ?? 'basic'));

const statusCopy = computed(() => {
    const status = subscription.value.status;
    if (status === 'active') {
        const until = subscription.value.access_until;
        const cancelOn = subscription.value.is_canceling ? subscription.value.cancel_at : null;
        let message = until ? `Active until ${formatIsoDate(until)}` : 'Active';
        if (cancelOn) {
            message += ` – cancels ${formatIsoDate(cancelOn)}`;
        }

        return message;
    }

    if (status === 'past_due') {
        const grace = subscription.value.grace_until;
        return grace ? `Past due – access ends ${formatIsoDate(grace)}` : 'Past due';
    }

    if (status === 'canceled') {
        return 'Canceled';
    }

    return 'No active subscription';
});

const showCancellationCta = computed(() => {
    return subscription.value.plan !== 'basic' && subscription.value.status !== 'canceled';
});

const cancellationCopy = computed(() => {
    if (!subscription.value.is_canceling) {
        return null;
    }

    if (subscription.value.cancel_at) {
        return `Your subscription will end on ${formatIsoDate(subscription.value.cancel_at)}. You will keep access until then.`;
    }

    return 'Your subscription will end at the close of the current billing period.';
});

function buildGroups(options) {
    const map = new Map();

    options.forEach((option) => {
        const key = option.plan;
        if (!map.has(key)) {
            map.set(key, {
                plan: key,
                planGroup: option.plan_group,
                displayName: option.display_name,
                description: option.description,
                options: [],
            });
        }

        map.get(key).options.push(option);
    });

    const groups = Array.from(map.values()).sort(
        (a, b) => planRank(a.plan) - planRank(b.plan),
    );

    groups.forEach((group) => {
        group.options.sort((a, b) => {
            const indexA = CADENCE_ORDER.indexOf(a.cadence ?? 'monthly');
            const indexB = CADENCE_ORDER.indexOf(b.cadence ?? 'monthly');
            return indexA - indexB;
        });
    });

    return groups;
}

const groupedPlans = computed(() => buildGroups(plans.value));

watch(
    groupedPlans,
    (groups) => {
        groups.forEach((group) => {
            if (!group.options.length) {
                return;
            }

            const currentCadence =
                group.plan === subscription.value.plan
                    ? subscription.value.cadence
                    : null;

            if (currentCadence && group.options.some((opt) => opt.cadence === currentCadence)) {
                selectedCadence[group.plan] = currentCadence;
                return;
            }

            if (!selectedCadence[group.plan]) {
                selectedCadence[group.plan] = group.options[0].cadence;
            }
        });
    },
    { immediate: true },
);

watch(
    () => [subscription.value.plan, subscription.value.cadence],
    () => {
        const group = groupedPlans.value.find((item) => item.plan === subscription.value.plan);
        if (group) {
            const match = group.options.find((opt) => opt.cadence === subscription.value.cadence);
            if (match) {
                selectedCadence[group.plan] = match.cadence;
            }
        }
    },
);

function formatCurrency(amountCents, currency = 'eur') {
    return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: currency?.toUpperCase() ?? 'EUR',
        minimumFractionDigits: 0,
    }).format(amountCents / 100);
}

function formatIsoDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function formatPrice(option) {
    // if (option.is_free) {
    //     return 'Free';
    // }

    const formatted = formatCurrency(option.amount_cents, option.currency);

    if (option.cadence === 'monthly') {
        return `${formatted} / month`;
    }

    if (option.cadence === 'yearly') {
        return `${formatted} / year`;
    }

    return formatted;
}

function pricingMeta(option) {
    const original = option.original_amount_cents ?? null;
    const savings = original && original > option.amount_cents
        ? original - option.amount_cents
        : null;

    return {
        originalFormatted: original ? formatCurrency(original, option.currency) : null,
        savingsFormatted: savings ? formatCurrency(savings, option.currency) : null,
        savingsAmount: savings,
    };
}

function yearlySavings(option, group) {
    if (option.cadence !== 'yearly') {
        return null;
    }

    const monthlyOption = group.options.find((opt) => opt.cadence === 'monthly');
    if (!monthlyOption) {
        return null;
    }

    const baseMonthly = monthlyOption.amount_cents;
    const yearlyFull = baseMonthly * 12;
    const savingsCents = yearlyFull - option.amount_cents;

    if (savingsCents <= 0) {
        return null;
    }

    const months = savingsCents / (monthlyOption.amount_cents || 1);

    return {
        amountFormatted: formatCurrency(savingsCents, option.currency),
        monthsSaved: Math.round(months),
    };
}

function getSelectedOption(group) {
    return (
        group.options.find((opt) => opt.cadence === selectedCadence[group.plan]) ?? group.options[0]
    );
}

function loadSubscription() {
    isLoading.value = true;
    return axios
        .get(route('billing.subscription.current'))
        .then(({ data }) => {
            subscription.value = {
                plan: data.plan ?? 'basic',
                status: data.status ?? 'inactive',
                cadence: data.cadence ?? null,
                access_until: data.access_until ?? null,
                grace_until: data.grace_until ?? null,
                cancel_at: data.cancel_at ?? null,
                canceled_at: data.canceled_at ?? null,
                is_canceling: data.is_canceling ?? false,
            };
            plans.value = data.available_plans ?? [];
            needsPaymentMethod.value = data.needs_payment_method ?? false;
            setupUrl.value = null;
        })
        .catch((error) => {
            console.error(error);
            errorMessage.value =
                'We could not load your billing status right now. Please refresh the page.';
        })
        .finally(() => {
            isLoading.value = false;
        });
}

function startCheckout(option) {
    if (!option || option.is_free) {
        errorMessage.value = 'Contact support to downgrade to Basic.';
        return;
    }

    axios
        .post(route('billing.subscription.checkout'), {
            plan: option.plan,
            cadence: option.cadence,
        })
        .then(({ data }) => {
            if (data?.status === 'support_notified') {
                errorMessage.value = 'Our support team has been notified to help with this change. Sit tight!';
                return;
            }

            if (data?.url) {
                window.open(data.url, '_self', 'noopener');
            }
        })
        .catch((error) => {
            console.error(error);
            const apiMessage = error.response?.data?.error;
            errorMessage.value = apiMessage ?? 'Unable to start checkout. Please try again shortly.';
        });
}

function requestCardSetup() {
    axios
        .post(route('billing.subscription.cardSetup'))
        .then(({ data }) => {
            if (data?.url) {
                setupUrl.value = data.url;
                window.open(data.url, '_blank', 'noopener');
            }
        })
        .catch((error) => {
            console.error(error);
            errorMessage.value = 'Unable to start card setup right now. Please try again soon.';
        });
}

function cancelSubscription() {
    if (isCancelling.value) {
        return;
    }

    isCancelling.value = true;
    errorMessage.value = null;

    axios
        .delete(route('billing.subscription.cancel'))
        .then(({ data }) => {
            const status = data?.status ?? null;
            const cancelAt = data?.cancel_at ?? subscription.value.cancel_at;
            const formattedDate = cancelAt ? formatIsoDate(cancelAt) : null;

            if (status === 'support_notified') {
                successMessage.value = 'Our support team has been notified to finish your cancellation manually.';
            } else if (status === 'already_scheduled') {
                successMessage.value = formattedDate
                    ? `Your cancellation is already scheduled. You will retain access until ${formattedDate}.`
                    : 'Your cancellation is already scheduled for the end of the current billing period.';
            } else if (status === 'scheduled') {
                successMessage.value = formattedDate
                    ? `We scheduled your cancellation. You will retain access until ${formattedDate}.`
                    : 'We scheduled your cancellation at the end of the current billing period.';
            } else {
                successMessage.value = 'We are processing your cancellation. Please check back shortly.';
            }

            return loadSubscription();
        })
        .catch((error) => {
            console.error(error);
            const apiMessage = error.response?.data?.error;
            errorMessage.value = apiMessage ?? 'Unable to cancel your subscription right now. Please try again soon.';
        })
        .finally(() => {
            isCancelling.value = false;
        });
}

function setCadence(planCode, cadence) {
    selectedCadence[planCode] = cadence;
}

function isCurrentOption(group, option) {
    return (
        group.plan === subscription.value.plan &&
        option.cadence === subscription.value.cadence
    );
}

function planDisplayName(group) {
    if (group.displayName) {
        return group.displayName;
    }

    return planHeadline(group.plan);
}

function planHeadline(planCode) {
    switch (planCode) {
        case 'essentials':
            return 'Essentials';
        case 'pro':
            return 'Pro';
        default:
            return 'Basic';
    }
}

function planDescription(planCode) {
    switch (planCode) {
        case 'essentials':
            return 'For teams ready to automate sales and marketing';
        case 'pro':
            return 'Full revenue platform with advanced automations';
        default:
            return 'Get started with core tools for free';
    }
}

function cadenceLabel(option) {
    if (option.cadence === 'yearly') {
        return 'Yearly <span class="block text-xxs opacity-70">(2 months free)</span>';
    }

    if (option.cadence === 'monthly') {
        return 'Monthly';
    }

    return 'One-time';
}

function ctaLabel(group, option) {
    if (isCurrentOption(group, option)) {
        return 'Manage billing in Stripe';
    }

    if (group.plan === subscription.value.plan) {
        return option.cadence === 'yearly'
            ? 'Switch to yearly billing'
            : 'Switch to monthly billing';
    }

    const targetRank = planRank(group.plan);
    if (targetRank > currentPlanRank.value) {
        return `Upgrade to ${planDisplayName(group)}`;
    }

    if (targetRank < currentPlanRank.value) {
        return `Downgrade to ${planDisplayName(group)}`;
    }

    return `Switch to ${planDisplayName(group)}`;
}

function isCtaDisabled(group, option) {
    if (option.is_free) {
        return group.plan === subscription.value.plan;
    }

    return isCurrentOption(group, option);
}

function handleAction(group) {
    const option = getSelectedOption(group);
    startCheckout(option);
}

const tierBadgeClass = computed(() => {
    const status = subscription.value.status;
    if (status === 'active') {
        return 'bg-myflowGreen-900 text-white border border-myflowGreen-900 w-full text-center justify-center py-4';
    }
    if (status === 'past_due') {
        return 'bg-amber-100 text-amber-800 border border-amber-200';
    }
    return 'bg-gray-100 text-gray-700 border border-gray-200';
});

onMounted(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('checkout') === 'success') {
        document.getElementById('upgrade-top-banner')?.remove();
        isLoading.value = true;
        setTimeout(() => {
            loadSubscription();
            isLoading.value = false;
            showConfetti.value = true;
        }, 5000)
    } else {
        loadSubscription();
    }
});
</script>

<template>
    <div class="w-full">
        <div class="max-w-5xl px-4 py-12 mx-auto">
            <div class="mb-10 text-center">
                <h1 class="text-3xl font-semibold text-slate-900">Choose the plan that matches your growth</h1>
                <p class="max-w-2xl mx-auto mt-3 text-slate-600">
                    Keep your customer journeys, sales automations, and expert experiences running in one place. Upgrade when you are ready—your data stays exactly where it is.
                </p>

                <div
                    v-if="!isLoading"
                    class="inline-flex items-center gap-2 px-4 py-2 mt-6 text-sm rounded-full"
                    :class="tierBadgeClass"
                >
                    <strong class="tracking-wide uppercase">{{ planHeadline(subscription.plan) }}</strong>
                    <span class="text-white/80" v-if="subscription.status === 'active'">•</span>
                    <span>{{ statusCopy }}</span>
                </div>
            </div>

            <div v-if="errorMessage" class="max-w-3xl mx-auto mb-8">
                <div class="px-4 py-3 text-red-800 border border-red-200 rounded bg-red-50">
                    {{ errorMessage }}
                </div>
            </div>

            <div v-if="successMessage" class="max-w-3xl mx-auto mb-8">
                <div class="px-4 py-3 text-green-800 border border-green-200 rounded bg-green-50">
                    {{ successMessage }}
                </div>
            </div>

            <div v-if="isLoading" class="flex justify-center py-20">
                <div class="inline-flex flex-col items-center justify-center px-4 py-2 text-sm border rounded-lg text-slate-600 border-slate-200">
                    <mf-spinner-medium class="w-12 h-6 mx-auto"></mf-spinner-medium>
                    <div class="pt-2">Loading billing information…</div>
                </div>
            </div>

            <div v-else class="space-y-8">
                <div class="grid gap-6 lg:grid-cols-3">
                    <article
                        v-for="group in groupedPlans"
                        :key="group.plan"
                        class="flex flex-col h-full bg-white border shadow-sm rounded-2xl"
                        :class="{
                            'border-myflowGreen-900 shadow-myflowGreen-100 ring-2 ring-myflowGreen-100': group.plan === subscription.plan,
                        }"
                    >
                        <div class="px-6 py-6 space-y-4 border-b mb-6">
                            <div class="flex items-center justify-between">
                                <h2 class="text-xl font-semibold text-slate-900">{{ planDisplayName(group) }}</h2>
                                <span
                                    v-if="group.plan === subscription.plan"
                                    class="inline-flex items-center px-3 py-1 text-xs font-semibold uppercase rounded-full bg-myflowGreen-100 text-myflowGreen-900"
                                >
                                    Your plan
                                </span>
                            </div>
                            <p class="text-sm text-slate-600">
                                {{ group.description ?? planDescription(group.plan) }}
                            </p>

                            <div
                                v-if="group.options.length > 1"
                                class="flex items-stretch gap-2 p-1 border rounded-full border-slate-200 bg-slate-50"
                            >
                                <button
                                    v-for="option in group.options"
                                    :key="`${group.plan}-${option.cadence}`"
                                    type="button"
                                    class="flex-1 px-4 py-2 text-xs font-semibold transition rounded-full whitespace-nowrap"
                                    :class="option.cadence === selectedCadence[group.plan]
                                        ? 'bg-myflowGreen-900 text-white shadow-sm'
                                        : 'text-slate-600'"
                                    @click="setCadence(group.plan, option.cadence)"
                                >
                                    <span v-html="cadenceLabel(option)"></span>
                                </button>
                            </div>

                            <div
                                v-for="option in [getSelectedOption(group)]"
                                :key="`${group.plan}-${option.cadence}-price`"
                                class="space-y-1"
                            >
                                <template v-for="meta in [pricingMeta(option)]" :key="`${group.plan}-${option.cadence}-meta`">
                                    <p
                                        v-if="meta.originalFormatted"
                                        class="flex items-center gap-2 text-sm text-slate-500"
                                    >
                                        <span>Usually</span>
                                        <span class="line-through">
                                            {{ meta.originalFormatted }}
                                        </span>
                                        <span
                                            v-if="meta.savingsAmount"
                                            class="uppercase text-xs bg-myflowGreen-100 text-myflowGreen-900 px-2 py-0.5 rounded-full"
                                        >
                                            Save {{ meta.savingsFormatted }}
                                        </span>
                                    </p>
                                    <p class="text-3xl font-semibold text-slate-900">
                                        {{ formatPrice(option) }}
                                    </p>
                                    <p
                                        v-if="option.cadence === 'monthly'"
                                        class="text-xs text-slate-500"
                                    >
                                        Billed monthly
                                    </p>
                                    <p
                                        v-if="option.cadence === 'yearly'"
                                        class="text-xs text-slate-500"
                                    >
                                        Billed yearly
                                    </p>
                                    <p
                                        v-if="yearlySavings(option, group)"
                                        class="mt-2 text-xs text-myflowGreen-900"
                                    >
                                        Save
                                        {{ yearlySavings(option, group).amountFormatted }}
                                        ({{ yearlySavings(option, group).monthsSaved }} months free)
                                    </p>
                                </template>
                            </div>
                        </div>

<!--                        <ul class="flex-1 px-6 py-6 space-y-3 text-sm text-slate-700">-->
<!--                            <li-->
<!--                                v-for="feature in featureMatrix[group.plan] ?? []"-->
<!--                                :key="feature"-->
<!--                                class="flex items-start gap-3"-->
<!--                            >-->
<!--                                <svg class="w-4 h-4 mt-1 text-myflowGreen-900 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">-->
<!--                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z" clip-rule="evenodd" />-->
<!--                                </svg>-->
<!--                                <span>{{ feature }}</span>-->
<!--                            </li>-->
<!--                        </ul>-->

                        <div class="px-6 pb-6 mt-auto">
                            <p
                                v-if="getSelectedOption(group).is_free && group.plan !== subscription.plan"
                                class="mb-3 text-xs text-slate-500"
                            >
                                Downgrading to Basic? Contact support so we can update billing immediately.
                            </p>
                            <button
                                type="button"
                                class="inline-flex items-center justify-center w-full gap-2 px-5 py-3 text-sm font-semibold transition rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                                :class="isCtaDisabled(group, getSelectedOption(group))
                                    ? 'border border-myflowGreen-900 text-myflowGreen-900 bg-myflowGreen-100 focus-visible:ring-myflowGreen-900 cursor-not-allowed'
                                    : 'border border-myflowGreen-900 text-white bg-myflowGreen-900 hover:bg-myflowGreen-800 focus-visible:ring-myflowGreen-900'"
                                :disabled="isCtaDisabled(group, getSelectedOption(group))"
                                @click="handleAction(group)"
                            >
                                {{ ctaLabel(group, getSelectedOption(group)) }}
                            </button>
                        </div>
                    </article>
                </div>
            </div>

            <div class="flex justify-center mt-6">
                <a
                    href="https://myflow.io/pricing"
                    target="_blank"
                    class="font-semibold mf-link"
                >
                    <button
                        type="button"
                        class="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition border rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 bg-white"
                    >
                        Compare plans and features
                    </button>
                </a>
            </div>

            <company-myflow-billing-card-setup
                v-if="needsPaymentMethod"
                :url="setupUrl"
                @request-setup="requestCardSetup"
            />

            <div
                v-if="showCancellationCta"
                class="mx-auto mt-6"
            >
                <div class="flex flex-col gap-4 px-6 py-5 border rounded-2xl border-slate-200 bg-slate-50 md:flex-row md:items-center md:justify-between">
                    <div class="space-y-1 text-sm text-slate-700">
                        <p v-if="cancellationCopy">{{ cancellationCopy }}</p>
                        <p v-else class="mb-0">Need to leave myflow Pro?<br> Cancel now and keep access until your current billing period ends.</p>
                        <p v-if="subscription.is_canceling" class="mb-0 text-xs text-slate-500">
                            Changed your mind?<br> Pick a paid plan again before the end date to keep everything running.
                        </p>
                    </div>
                    <button
                        type="button"
                        class="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold transition border rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                        :class="(isCancelling || subscription.is_canceling)
                                ? 'border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed focus-visible:ring-slate-200'
                                : 'border-red-200 bg-white text-red-700 hover:bg-red-50 focus-visible:ring-red-500'"
                        :disabled="isCancelling || subscription.is_canceling"
                        @click="cancelSubscription"
                    >
                        <span v-if="isCancelling">Cancelling…</span>
                        <span v-else-if="subscription.is_canceling">Cancellation scheduled</span>
                        <span v-else>Cancel subscription</span>
                    </button>
                </div>
            </div>


        </div>

        <confetti-bomb v-if="showConfetti" />
    </div>
</template>
